import React from 'react';
// import { useTranslation } from 'react-i18next';

/**
 * Rules text component that adds our rules as paragraphs.
 * Usage: `<RulesText />`
 */
export default function RulesText() {
  // const { t } = useTranslation('pages');

  return (
    <>
      <p>
        <strong>💳 HostFolk Terms and Conditions</strong>
        <br />
        <strong>Effective Date:</strong> 08/09/2025
        <br />
        <strong>Jurisdiction:</strong> Republic of Ireland &amp; European Union
      </p>
      <p>
        Welcome to HostFolk! By creating an account and using our platform, you
        agree to the following payment terms, which govern how bookings, fees,
        and payouts are handled under our marketplace model.
      </p>

      <h3>1. About HostFolk</h3>
      <p>
        HostFolk is a homestay platform that connects host families and
        international guests—including exchange students and independent
        travellers—for meaningful cultural exchange and temporary accommodation.
      </p>

      <h3>2. Eligibility</h3>
      <ul>
        <li>
          Hosts must be 18 years or older and legally entitled to offer
          accommodation in Ireland.
        </li>
        <li>
          Guests must be at least 16 years old or accompanied by a legal
          guardian.
        </li>
        <li>
          All users must provide accurate personal information and agree to
          respectful conduct.
        </li>
      </ul>

      <h3>3. User Responsibilities</h3>
      <p>
        <strong>Hosts:</strong>
      </p>
      <ul>
        <li>
          Ensure the accommodation is safe, clean, and accurately described.
        </li>
        <li>Comply with Irish housing, tax, and insurance regulations.</li>
        <li>
          Respect guests’ privacy, cultural background, and dietary or religious
          needs.
        </li>
      </ul>
      <p>
        <strong>Guests:</strong>
      </p>
      <ul>
        <li>Treat the host’s home with care and follow house rules.</li>
        <li>
          Communicate clearly regarding arrival times, expectations, and special
          needs.
        </li>
        <li>Refrain from illegal or disruptive behaviour during the stay.</li>
      </ul>

      <h3>4. Marketplace Payment Structure</h3>
      <p>
        HostFolk operates as a homestay marketplace. We facilitate connections
        between hosts and guests and enable secure payments through a
        third-party payment provider. HostFolk does not collect, hold, or
        disburse funds directly.
      </p>

      <h3>5. Booking Payments</h3>
      <ul>
        <li>
          Guests pay for bookings via HostFolk’s integrated payment system,
          powered by a licensed third-party payment provider.
        </li>
        <li>
          The payment provider processes the transaction and transfers the
          appropriate payout directly to the host, minus applicable fees.
        </li>
        <li>
          HostFolk does not act as a merchant of record or hold funds on behalf
          of users.
        </li>
      </ul>

      <h3>6. Service Fees</h3>
      <ul>
        <li>
          HostFolk charges a platform service fee to hosts and/or guests, which
          is automatically deducted by the payment provider during the
          transaction.
        </li>
        <li>
          The fee amount is clearly displayed before booking confirmation.
        </li>
        <li>
          Service fees help support platform operations, customer support, and
          technology infrastructure.
        </li>
      </ul>

      <h3>7. Host Payouts</h3>
      <ul>
        <li>
          Hosts receive payouts directly from the payment provider, typically
          after guest check-in and subject to the provider’s processing
          timeline.
        </li>
        <li>
          Hosts must set up and verify their payout method (e.g., bank account,
          digital wallet) with the payment provider during onboarding.
        </li>
        <li>
          HostFolk is not responsible for delays, errors, or disputes related to
          payout processing.
        </li>
      </ul>

      <h3>8. Tax Obligations</h3>
      <ul>
        <li>
          Hosts are responsible for reporting income to Revenue (Irish Tax and
          Customs) and complying with any applicable tax laws.
        </li>
        <li>HostFolk does not withhold taxes or provide tax advice.</li>
        <li>
          Hosts may be required to submit tax-related documentation to the
          payment provider, depending on local regulations.
        </li>
      </ul>

      <h3>9. Cancellations &amp; Refunds</h3>
      <ul>
        <li>
          Cancellation policies are set by hosts and must be clearly stated in
          their listings.
        </li>
        <li>
          Refunds to guests are processed by the payment provider in accordance
          with the host’s policy and HostFolk’s dispute resolution guidelines.
        </li>
        <li>
          HostFolk may facilitate communication but does not issue refunds
          directly.
        </li>
      </ul>

      <h3>10. Disputes &amp; Chargebacks</h3>
      <ul>
        <li>
          In the event of a payment dispute or chargeback, HostFolk may assist
          in mediation but final resolution is handled by the payment provider.
        </li>
        <li>
          Hosts may be liable for chargebacks if accommodation is not provided
          as agreed.
        </li>
      </ul>

      <h3>11. Currency &amp; Conversion</h3>
      <ul>
        <li>All transactions are processed in [EUR or local currency].</li>
        <li>
          Currency conversion fees may apply for international users and are
          managed by the payment provider.
        </li>
      </ul>

      <h3>12. Modifications to Payment Terms</h3>
      <p>
        HostFolk may update these terms from time to time. Continued use of the
        platform after changes are posted constitutes acceptance of the revised
        terms.
      </p>

      <h3>13. Safety &amp; Conduct</h3>
      <ul>
        <li>
          Discrimination, harassment, or abuse of any kind is strictly
          prohibited.
        </li>
        <li>HostFolk encourages open communication and mutual respect.</li>
        <li>
          Users may be suspended or removed for violating these standards.
        </li>
      </ul>

      <h3>14. Insurance &amp; Liability</h3>
      <ul>
        <li>
          Hosts are advised to review their home insurance policies to ensure
          coverage for homestay arrangements.
        </li>
        <li>
          HostFolk is not liable for personal injury, property damage, or other
          losses incurred during a homestay.
        </li>
        <li>
          Users agree to indemnify HostFolk against claims arising from their
          use of the platform.
        </li>
      </ul>

      <h3>15. Privacy &amp; Data Protection</h3>
      <p>
        HostFolk processes personal data in accordance with the General Data
        Protection Regulation (GDPR) and the Data Protection Act 2018 (Ireland).
        For full details, please refer to our Privacy Policy.
      </p>

      <h3>16. Intellectual Property</h3>
      <p>
        All content on the HostFolk website—including text, images, and
        branding—is the property of HostFolk and may not be reproduced without
        written permission.
      </p>

      <h3>17. Modifications to Terms</h3>
      <p>
        HostFolk reserves the right to update these Terms at any time. Continued
        use of the platform after changes are posted constitutes acceptance of
        the revised Terms.
      </p>

      <h3>18. Governing Law</h3>
      <p>
        These Terms are governed by the laws of the Republic of Ireland. Any
        disputes arising from these Terms shall be subject to the exclusive
        jurisdiction of the Irish courts.
      </p>

      <h3>19. Contact Information</h3>
      <p>
        Email: termsandConditions@hostfolk.com
        <br />
        Website: www.hostfolk.com
      </p>
    </>
  );
}

RulesText.propTypes = {};
